import { Box, Popover, Skeleton, Stack, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { GiornoFascia, StatoDati } from '../../hooks/usePresenzeGiorni';
import { formatDettaglioAssenza } from '../../utility_objects/AssenzeOperatoriUtils';
import {
  AssenteDelGiorno,
  formatGiornoEsteso,
  formatTitoloGiorno,
  getAssentiDaElencare,
  PersonaInServizio,
} from '../../utility_objects/PresenzeUtils';
import { ChipPresenza } from './ChipPresenza';

// Pallino del colore dell'operatore, come nell'agenda e nei turni.
const Pallino = memo(function Pallino({
  color,
}: {
  color: string;
}): JSX.Element {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: color,
      }}
    />
  );
});

// Una riga per persona: nome, fasce orarie e, se è assente in parte, la chip del
// tipo col solo bordo. Stessa impaginazione di FOCUS CLOUD. Se la riga non basta
// si accorcia il nome e non l'orario: il popover è l'unico punto in cui le fasce
// si leggono per intero.
const VoceInServizio = memo(function VoceInServizio({
  persona,
}: {
  persona: PersonaInServizio;
}): JSX.Element {
  return (
    <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
      <Pallino color={persona.color} />
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {persona.username}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ flexShrink: 0 }}
      >
        {persona.orario}
      </Typography>
      {persona.assenzaParziale && (
        <ChipPresenza
          label={persona.assenzaParziale}
          stato="inParte"
          tipo={persona.assenzaParziale}
        />
      )}
    </Stack>
  );
});

// Giornata intera: chip tinta e periodo. Assenza in parte di chi non è fra gli in
// servizio: chip col solo bordo, come nella cella, e dopo il periodo l'orario di
// presenza. Il dettaglio sta sotto il nome e va a capo: con due fasce orarie su una
// riga sola i puntini taglierebbero proprio l'orario. Come in FOCUS CLOUD.
const VoceAssente = memo(function VoceAssente({
  assente,
}: {
  assente: AssenteDelGiorno;
}): JSX.Element {
  return (
    <Stack sx={{ gap: 0.25 }}>
      <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <Pallino color={assente.color} />
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {assente.username}
        </Typography>
        <ChipPresenza
          label={assente.tipo}
          // Tinta = assente tutto il giorno; solo bordo = in servizio in parte.
          stato={assente.tuttoIlGiorno ? 'assente' : 'inParte'}
          tipo={assente.tipo}
        />
      </Stack>
      {/* Rientro pari a pallino e spazio, così il dettaglio parte sotto il nome. */}
      <Typography variant="caption" color="text.secondary" sx={{ pl: 2.25 }}>
        {formatDettaglioAssenza(assente)}
      </Typography>
    </Stack>
  );
});

// Stato di una sezione senza elenco: in caricamento, in errore o vuota. Con
// l'elenco le voci stanno direttamente nello Stack della sezione.
const StatoSezione = memo(function StatoSezione({
  stato,
  errore,
  vuota,
}: {
  stato: StatoDati;
  errore: string;
  vuota: string;
}): JSX.Element {
  if (stato === 'caricamento') return <Skeleton variant="text" width="60%" />;
  return (
    <Typography variant="body2" color="text.secondary">
      {stato === 'errore' ? errore : vuota}
    </Typography>
  );
});

// Il doppio click, che nel resto della griglia apre «nuovo appuntamento», capita
// anche sulla fascia: il primo click apre il popover e il secondo cade sul suo
// backdrop invisibile, che lo richiuderebbe subito. In un click `detail` è il
// numero di click della sequenza: dal secondo in poi il backdrop non chiude.
const isClickRipetuto = (event: object): boolean =>
  'detail' in event && typeof event.detail === 'number' && event.detail > 1;

interface PopoverPresenzeProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  giorno: GiornoFascia;
  statoAssenze: StatoDati;
}

/**
 * Dettaglio di un giorno della fascia Presenze, in sola lettura: chi è in servizio
 * con le fasce orarie (con la chip del tipo se è assente in parte) e chi è assente,
 * con il periodo. Ogni persona compare una volta sola. Stessa impaginazione di
 * FOCUS CLOUD.
 */
export const PopoverPresenze = memo(function PopoverPresenze({
  anchorEl,
  onClose,
  giorno,
  statoAssenze,
}: PopoverPresenzeProps): JSX.Element {
  const onClosePopover = useCallback(
    (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && isClickRipetuto(event)) return;
      onClose();
    },
    [onClose]
  );
  const titolo = formatTitoloGiorno(giorno.giorno);
  const inServizio = giorno.inServizio ?? [];
  // Chi è assente solo in parte compare fra le persone in servizio, con la chip
  // accanto, e fra gli assenti solo se lì non c'è (come nella cella).
  const assenti = getAssentiDaElencare(giorno);

  return (
    <Popover
      open={anchorEl !== null}
      anchorEl={anchorEl}
      onClose={onClosePopover}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      // PaperProps e non slotProps.paper: con MUI 5.10 slotProps non c'è.
      PaperProps={{
        role: 'dialog',
        'aria-label': `Presenze di ${formatGiornoEsteso(giorno.giorno)}`,
        sx: {
          width: '100%',
          maxWidth: 'min(360px, calc(100% - 32px))',
          maxHeight: '60vh',
        },
      }}
    >
      <Stack spacing={1.5} sx={{ p: 2 }}>
        <Typography variant="subtitle1">{titolo}</Typography>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2">
            {giorno.inServizio
              ? `In servizio (${inServizio.length})`
              : 'In servizio'}
          </Typography>
          {giorno.statoInServizio === 'pronto' && inServizio.length > 0 ? (
            inServizio.map((persona) => (
              <VoceInServizio key={persona.userId} persona={persona} />
            ))
          ) : (
            <StatoSezione
              stato={giorno.statoInServizio}
              errore="Non disponibile"
              vuota="Nessuno in servizio"
            />
          )}
        </Stack>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2">
            {giorno.assenti ? `Assenti (${assenti.length})` : 'Assenti'}
          </Typography>
          {statoAssenze === 'pronto' && assenti.length > 0 ? (
            assenti.map((assente) => (
              <VoceAssente key={assente.userId} assente={assente} />
            ))
          ) : (
            <StatoSezione
              stato={statoAssenze}
              errore="Assenze non disponibili"
              vuota="Nessun assente"
            />
          )}
        </Stack>
      </Stack>
    </Popover>
  );
});
